import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { Pagination } from '@/components';
import { Button } from '@/design-system/button';
import { Card } from '@/design-system/card';
import { Input, InputIcon, InputRoot } from '@/design-system/input';
import { Skeleton } from '@/design-system/skeleton';
import { Tag } from '@/design-system/tag';

import { ZoneBar, ZoneStatusBadge } from '../../components/OccupancyBadge';
import { EnumSelect } from '../../components/form';
import { fmtQty } from '../../format';
import { type LoadState, useLoad } from '../../hooks/useLoad';
import {
  fmtAmount,
  fmtPct,
  LOCATION_TYPE_LABEL,
  usedOfCapacity,
  UTILIZATION_BASIS,
  UTILIZATION_FILTERS,
  type UtilizationFilter,
} from '../../occupancy';
import { WAREHOUSE_PATHS } from '../../paths';
import type {
  LocationUtilization,
  Pagination as PageInfo,
  StorageOccupancyChild,
  StorageOccupancyChildren,
  UtilizationBasis,
  UtilizationFigures,
  ZoneOccupancyThresholds,
} from '../../types';

/** Rows per page of a node's children (sections of a location, bins of a section). */
export const CHILDREN_LIMIT = 12;

/** Fetch one page of a node's children (on the page's basis). */
export type LoadChildren = (
  parentID: string,
  page: number,
  signal: AbortSignal
) => Promise<StorageOccupancyChildren>;

/** "· 2 bin tanpa kapasitas" when some bins are left out, else "". */
const withoutCapacitySuffix = (f: Pick<UtilizationFigures, 'binsWithoutCapacity'>): string =>
  f.binsWithoutCapacity > 0
    ? ` · ${fmtQty(f.binsWithoutCapacity)} bin tanpa kapasitas`
    : '';

/**
 * A location row's sub-line: "1,2 / 5 m³ · 3,8 tersisa" (or "kapasitas belum
 * diatur"), plus "· 2 bin tanpa kapasitas" when some bins are left out.
 */
export const locationUsedLine = (
  l: Pick<UtilizationFigures, 'used' | 'capacity' | 'free' | 'binsWithoutCapacity'>,
  basis: UtilizationBasis
): string =>
  (l.capacity > 0
    ? `${usedOfCapacity(l, basis)} · ${UTILIZATION_BASIS[basis].fmt(l.free)} tersisa`
    : 'kapasitas belum diatur') + withoutCapacitySuffix(l);

/** A section row: "1,2 / 5 m³" (or "kapasitas belum diatur"), bins left out. */
export const sectionUsedLine = (
  s: Pick<UtilizationFigures, 'used' | 'capacity' | 'binsWithoutCapacity'>,
  basis: UtilizationBasis
): string =>
  (s.capacity > 0 ? usedOfCapacity(s, basis) : 'kapasitas belum diatur') +
  withoutCapacitySuffix(s);

/** A bin row: "1,2 / 2 m³", or "kapasitas belum diatur · 0,4 m³ stok". */
export const binUsedLine = (
  b: Pick<UtilizationFigures, 'used' | 'capacity' | 'usedWithoutCapacity'>,
  basis: UtilizationBasis
): string =>
  b.capacity > 0
    ? usedOfCapacity(b, basis)
    : `kapasitas belum diatur${b.usedWithoutCapacity > 0 ? ` · ${fmtAmount(b.usedWithoutCapacity, basis)} stok` : ''}`;

/** Locations per page of the list (the server caps at 48). */
export const ZONE_PAGE_SIZES = [12, 24, 48];

/** Link to the Stock page filtered to one location. */
export const zoneStockLink = (storageID: string): string =>
  `${WAREHOUSE_PATHS.stock}?locationID=${encodeURIComponent(storageID)}`;

/** Width of the "Lihat stok di lokasi ini" column (row link + header cell). */
const LINK_COL = 'md:w-[170px] md:text-right';

const rowBtn =
  'flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg px-2 py-2 text-left hover:bg-bg-weak-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--theme-accent-base)';

const pctCls =
  'w-12 text-right text-[13px] font-semibold tabular-nums text-text-strong-950';

/**
 * Sebelumnya / Berikutnya under a node's children; when they fit one page,
 * just how many there are ("8 rak").
 */
const Pager: React.FC<{
  pagination: StorageOccupancyChildren['pagination'];
  unit: string;
  loading?: boolean;
  id: string;
  onPage: (page: number) => void;
}> = ({ pagination: p, unit, loading, id, onPage }) => {
  if (p.total <= p.size) {
    return (
      <p
        className="mt-1 px-2 py-1 text-[12px] tabular-nums text-text-soft-400"
        data-testid={`occ-detail-count-${id}`}
      >
        {fmtQty(p.total)} {unit}
      </p>
    );
  }
  const from = (p.page - 1) * p.size + 1;
  const to = Math.min(p.total, p.page * p.size);
  return (
    <div
      className="mt-1 flex items-center justify-between gap-2 px-2 py-1 text-[12px] text-text-sub-600"
      data-testid={`occ-detail-pager-${id}`}
    >
      <span data-testid={`occ-detail-range-${id}`}>
        {from}–{to} dari {fmtQty(p.total)} {unit}
      </span>
      <div className="flex items-center gap-1">
        <Button
          tone="neutral"
          style="stroke"
          size="xs"
          disabled={p.page <= 1 || loading}
          onClick={() => onPage(p.page - 1)}
          data-testid={`occ-detail-prev-${id}`}
        >
          Sebelumnya
        </Button>
        <Button
          tone="neutral"
          style="stroke"
          size="xs"
          disabled={p.page >= p.lastPage || loading}
          onClick={() => onPage(p.page + 1)}
          data-testid={`occ-detail-next-${id}`}
        >
          Berikutnya
        </Button>
      </div>
    </div>
  );
};

/**
 * The opened part of a node: its children (one server page), loading /
 * empty / error states and the pager — all scoped to the node.
 */
const NodeChildren: React.FC<{
  id: string;
  name: string;
  unit: 'rak' | 'bin';
  kids: LoadState<StorageOccupancyChildren>;
  onPage: (page: number) => void;
  renderChild: (child: StorageOccupancyChild) => React.ReactNode;
}> = ({ id, name, unit, kids, onPage, renderChild }) => (
  <div
    className="ml-[17px] border-l border-stroke-soft-200 pl-2"
    aria-busy={kids.loading && kids.data ? 'true' : undefined}
    data-testid={`occ-children-${id}`}
  >
    {kids.error ? (
      <p className="px-3 py-2 text-[12px] text-(--state-error-base)">
        {kids.error.message}{' '}
        <button
          type="button"
          className="font-semibold underline underline-offset-2"
          onClick={kids.reload}
          data-testid={`occ-children-retry-${id}`}
        >
          Coba lagi
        </button>
      </p>
    ) : !kids.data ? (
      <p className="px-3 py-2 text-[12px] text-text-sub-600" aria-busy="true">
        Memuat {unit}…
      </p>
    ) : kids.data.data.length === 0 ? (
      <p
        className="px-3 py-2 text-[12px] text-text-sub-600"
        data-testid={`occ-children-empty-${id}`}
      >
        {unit === 'rak'
          ? 'Belum ada rak di lokasi ini.'
          : 'Belum ada bin di rak ini.'}
      </p>
    ) : (
      <>
        <ul
          className={`flex flex-col transition-opacity ${kids.loading ? 'opacity-60' : ''}`}
          aria-label={`${unit} di ${name}, paling penuh dulu`}
        >
          {kids.data.data.map(renderChild)}
        </ul>
        <Pager
          pagination={kids.data.pagination}
          unit={unit}
          loading={kids.loading}
          id={id}
          onPage={onPage}
        />
      </>
    )}
  </div>
);

/**
 * A bin of a section: used / capacity on the basis, percent and status
 * (its rule as tooltip), and the units it holds when the basis is not units.
 */
const BinRow: React.FC<{
  bin: StorageOccupancyChild;
  thresholds: ZoneOccupancyThresholds;
  basis: UtilizationBasis;
}> = ({ bin, thresholds, basis }) => {
  const id = bin.storageID;
  return (
    <li
      className="flex flex-wrap items-center gap-x-3 gap-y-1 px-2 py-1.5"
      data-testid={`occ-loc-${id}`}
    >
      <i className="ri-archive-line text-[14px] text-icon-soft-400" aria-hidden />
      <span className="min-w-0 flex-1 basis-40">
        <span className="block truncate text-[13px] text-text-strong-950">
          {bin.name}
        </span>
        <span
          className="block text-[11px] tabular-nums text-text-sub-600"
          data-testid={`occ-loc-used-${id}`}
        >
          {binUsedLine(bin, basis)}
          {basis !== 'unit' && bin.usedQty > 0 && (
            <span data-testid={`occ-loc-qty-${id}`}>
              {' '}
              · {fmtQty(bin.usedQty)} unit
            </span>
          )}
        </span>
      </span>
      <span className={pctCls} data-testid={`occ-loc-pct-${id}`}>
        {fmtPct(bin.pct)}
      </span>
      <ZoneStatusBadge
        status={bin.status}
        thresholds={thresholds}
        basis={basis}
        testId={`occ-loc-status-${id}`}
      />
    </li>
  );
};

/** A section (rak) of a location: used / capacity, bar, percent, status; opens its bins. */
const SectionNode: React.FC<{
  section: StorageOccupancyChild;
  thresholds: ZoneOccupancyThresholds;
  basis: UtilizationBasis;
  loadChildren: LoadChildren;
}> = ({ section, thresholds, basis, loadChildren }) => {
  const id = section.storageID;
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const kids = useLoad(
    (signal) => loadChildren(id, page, signal),
    [loadChildren, id, page],
    open
  );
  const noBins = section.binCount === 0;
  return (
    <li>
      <button
        type="button"
        className={`${rowBtn} w-full`}
        onClick={() => setOpen((o) => !o)}
        // A rack without bins has nothing to open: no dead-end click.
        disabled={noBins}
        aria-expanded={noBins ? undefined : open}
        title={
          noBins
            ? `${section.name} belum punya bin`
            : open
              ? `Tutup ${section.name}`
              : `Buka ${section.name}`
        }
        data-testid={`occ-loc-${id}`}
      >
        <i
          className={`${noBins ? 'ri-subtract-line text-icon-soft-400' : open ? 'ri-arrow-down-s-line text-icon-sub-600' : 'ri-arrow-right-s-line text-icon-sub-600'} text-[16px]`}
          aria-hidden
        />
        <span className="min-w-0 flex-1 basis-40">
          <span className="block truncate text-[13px] font-medium text-text-strong-950">
            {section.name}
          </span>
          {noBins ? (
            <span className="block text-[11px] text-text-soft-400">
              belum ada bin
            </span>
          ) : (
            <span
              className="block text-[11px] tabular-nums text-text-sub-600"
              data-testid={`occ-loc-used-${id}`}
            >
              {sectionUsedLine(section, basis)}
            </span>
          )}
        </span>
        {!noBins && (
          <span className="flex items-center gap-3">
            <ZoneBar
              pct={section.pct}
              status={section.status}
              label={`Utilisasi ${section.name}`}
              className="w-28 sm:w-36"
            />
            <span className={pctCls} data-testid={`occ-loc-pct-${id}`}>
              {fmtPct(section.pct)}
            </span>
            <ZoneStatusBadge
              status={section.status}
              thresholds={thresholds}
              basis={basis}
              testId={`occ-loc-status-${id}`}
            />
          </span>
        )}
      </button>
      {open && (
        <NodeChildren
          id={id}
          name={section.name}
          unit="bin"
          kids={kids}
          onPage={setPage}
          renderChild={(bin) => (
            <BinRow
              key={bin.storageID}
              bin={bin}
              thresholds={thresholds}
              basis={basis}
            />
          )}
        />
      )}
    </li>
  );
};

/**
 * One location: its type, used / capacity and room left on the basis, bar,
 * percent, status (its rule as tooltip) and the link to its
 * stock. The row opens the location's racks in place (12 per page, fetched
 * when first opened), a rack its bins.
 */
export const ZoneNode: React.FC<{
  zone: LocationUtilization;
  thresholds: ZoneOccupancyThresholds;
  basis: UtilizationBasis;
  loadChildren: LoadChildren;
}> = ({ zone, thresholds, basis, loadChildren }) => {
  const id = zone.storageID;
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const kids = useLoad(
    (signal) => loadChildren(id, page, signal),
    [loadChildren, id, page],
    open
  );
  return (
    <li className="py-0.5">
      <div className="flex items-center gap-2">
        <button
          type="button"
          className={rowBtn}
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          title={open ? `Tutup ${zone.name}` : `Lihat rak di ${zone.name}`}
          data-testid={`occ-zone-${id}`}
        >
          <i
            className={`${open ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'} text-[16px] text-icon-sub-600`}
            aria-hidden
          />
          <span className="min-w-0 flex-1 basis-48">
            <span className="flex min-w-0 items-center gap-2">
              <span className="truncate text-[13px] font-semibold text-text-strong-950">
                {zone.name}
              </span>
              <Tag
                variant="gray"
                size="xs"
                className="shrink-0"
                data-testid={`occ-zone-type-${id}`}
              >
                {LOCATION_TYPE_LABEL[zone.type] ?? zone.type}
              </Tag>
            </span>
            <span
              className="block text-[11px] tabular-nums text-text-sub-600"
              data-testid={`occ-zone-used-${id}`}
            >
              {locationUsedLine(zone, basis)}
            </span>
          </span>
          <span className="flex flex-wrap items-center gap-3">
            <ZoneBar
              pct={zone.pct}
              status={zone.status}
              label={`Utilisasi ${zone.name}`}
              className="w-28 sm:w-40"
            />
            <span className={pctCls} data-testid={`occ-zone-pct-${id}`}>
              {fmtPct(zone.pct)}
            </span>
            <span className="w-[150px]">
              <ZoneStatusBadge
                status={zone.status}
                thresholds={thresholds}
                basis={basis}
                testId={`occ-zone-status-${id}`}
              />
            </span>
          </span>
        </button>
        <Link
          to={zoneStockLink(id)}
          className={`${LINK_COL} shrink-0 whitespace-nowrap px-1 text-[12px] font-semibold text-accent underline-offset-4 hover:underline`}
          data-testid={`occ-zone-stock-${id}`}
        >
          Lihat stok di lokasi ini
        </Link>
      </div>
      {open && (
        <NodeChildren
          id={id}
          name={zone.name}
          unit="rak"
          kids={kids}
          onPage={setPage}
          renderChild={(section) => (
            <SectionNode
              key={section.storageID}
              section={section}
              thresholds={thresholds}
              basis={basis}
              loadChildren={loadChildren}
            />
          )}
        />
      )}
    </li>
  );
};

/** Column titles above the location rows (desktop only; widths mirror ZoneNode). */
const ZonesHead: React.FC = () => (
  <div
    className="hidden items-center gap-2 pb-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-text-soft-400 md:flex"
    data-testid="occ-zones-head"
    aria-hidden
  >
    <span className="flex flex-1 items-center gap-x-3 px-2">
      <span className="w-4" />
      <span className="flex-1">Lokasi</span>
      <span className="w-40">Terpakai</span>
      <span className="w-12 text-right">Utilisasi</span>
      <span className="w-[150px]">Status</span>
    </span>
    <span className={LINK_COL} />
  </div>
);

/** URL-backed search box state (`useUrlSearch`). */
export interface ZoneSearch {
  value: string;
  setValue: (v: string) => void;
  /** The settled term the request was made with. */
  committed: string;
}

const UTIL_OPTIONS = UTILIZATION_FILTERS.map((f) => ({
  value: f.value,
  label: f.label,
}));

/**
 * One server page of the hub's locations (fullest first, the server's
 * order), searched by name and filtered by utilization on the server, with
 * the pager under it. The hub card and the attention strip above are never
 * narrowed by it.
 */
export const ZoneList: React.FC<{
  zones: LocationUtilization[] | null;
  pagination: PageInfo | null;
  thresholds: ZoneOccupancyThresholds | null;
  basis: UtilizationBasis;
  /** The hub has at least one active location (search / filter or not). */
  hubHasZones: boolean;
  search: ZoneSearch;
  util: UtilizationFilter;
  onUtil: (value: UtilizationFilter) => void;
  /** A new page / search is on its way (the current one stays, dimmed). */
  loading: boolean;
  /** A page / search failed while an older answer is still shown. */
  error: string | null;
  onRetry: () => void;
  onPage: (page: number) => void;
  onLimit: (limit: number) => void;
  loadChildren: LoadChildren;
  /** Changes when every open node must close (hub / client switch). */
  resetKey: string;
}> = ({
  zones,
  pagination,
  thresholds,
  basis,
  hubHasZones,
  search,
  util,
  onUtil,
  loading,
  error,
  onRetry,
  onPage,
  onLimit,
  loadChildren,
  resetKey,
}) => {
  const narrowed = search.committed !== '' || util !== 'all';
  const showToolbar =
    zones !== null && (hubHasZones || search.value !== '' || util !== 'all');
  return (
    <Card padding="none" data-testid="occ-zones">
      <div className="flex flex-col gap-2 border-b border-stroke-soft-200 px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="text-base font-bold tracking-tight text-text-strong-950">
            Semua lokasi
          </h2>
          <p className="text-[12px] text-text-sub-600">
            Urut dari yang paling penuh. Klik lokasi untuk melihat rak dan bin
            di dalamnya.
          </p>
        </div>
        {showToolbar && (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <InputRoot size="sm" className="sm:w-56">
              <InputIcon>
                <i className="ri-search-line text-[14px]" aria-hidden />
              </InputIcon>
              <Input
                type="search"
                value={search.value}
                onChange={(e) => search.setValue(e.target.value)}
                placeholder="Cari nama lokasi…"
                aria-label="Cari nama lokasi"
                data-testid="occ-zone-search"
              />
            </InputRoot>
            <EnumSelect
              size="sm"
              className="sm:w-64"
              value={util}
              onChange={(v) => onUtil(v as UtilizationFilter)}
              options={UTIL_OPTIONS}
              aria-label="Filter utilisasi"
              data-testid="occ-pct-filter"
            />
          </div>
        )}
      </div>
      {error && zones && (
        <p
          className="border-b border-stroke-soft-200 px-4 py-2 text-[12px] text-(--state-error-base)"
          data-testid="occ-zones-error"
        >
          {error}{' '}
          <button
            type="button"
            className="font-semibold underline underline-offset-2"
            onClick={onRetry}
          >
            Coba lagi
          </button>
        </p>
      )}
      <div className="px-2 py-2">
        {!zones || !thresholds ? (
          <ul aria-busy="true" aria-label="Memuat lokasi" className="flex flex-col gap-2 p-2">
            {[0, 1, 2, 3].map((i) => (
              <li key={i} className="flex items-center gap-3">
                <Skeleton className="h-4 flex-1" />
                <Skeleton className="h-2 w-40" />
                <Skeleton className="h-4 w-10" />
              </li>
            ))}
          </ul>
        ) : zones.length === 0 && narrowed ? (
          <p
            className="px-3 py-8 text-center text-[13px] text-text-sub-600"
            data-testid="occ-zones-nomatch"
          >
            Tidak ada lokasi yang cocok dengan pencarian atau filter ini.
          </p>
        ) : zones.length === 0 ? (
          <p
            className="px-3 py-8 text-center text-[13px] text-text-sub-600"
            data-testid="occ-zones-empty"
          >
            Belum ada lokasi di hub ini. Tambahkan location di menu{' '}
            <Link
              to={WAREHOUSE_PATHS.storage}
              className="font-semibold text-accent underline-offset-4 hover:underline"
            >
              Storage
            </Link>
            .
          </p>
        ) : (
          <>
            <ZonesHead />
            <ul
              className={`flex flex-col transition-opacity ${loading ? 'opacity-60' : ''}`}
              aria-busy={loading ? 'true' : undefined}
              aria-label="Lokasi, paling penuh dulu"
            >
              {zones.map((zone) => (
                <ZoneNode
                  key={`${resetKey}:${zone.storageID}`}
                  zone={zone}
                  thresholds={thresholds}
                  basis={basis}
                  loadChildren={loadChildren}
                />
              ))}
            </ul>
          </>
        )}
      </div>
      {/* Always under a loaded list, one page or many: where you are and the page size. */}
      {zones && zones.length > 0 && pagination && pagination.total > 0 && (
        <Pagination
          className="flex-wrap border-t border-stroke-soft-200 px-4 py-3"
          data-testid="occ-zones-pager"
          currentPage={pagination.page}
          totalItems={pagination.total}
          itemsPerPage={pagination.size}
          onPageChange={onPage}
          onPageSizeChange={onLimit}
          pageSizeOptions={ZONE_PAGE_SIZES}
          pageSizeLabel="Tampilkan"
          pageSizeSuffix="lokasi / halaman"
          summary={
            <span data-testid="occ-zones-range">
              {(pagination.page - 1) * pagination.size + 1}–
              {Math.min(pagination.total, pagination.page * pagination.size)} dari{' '}
              {fmtQty(pagination.total)} lokasi
            </span>
          }
        />
      )}
    </Card>
  );
};
